import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import {
  Badge,
  Button,
  Collapse,
  Container,
  Progress,
  Table
} from 'reactstrap'
import { createConsumer } from '@rails/actioncable'
import moment from 'moment'
import PropTypes from 'prop-types'
import { v1 as uuidv1 } from 'uuid'

import { useTranslation } from 'react-i18next'

import AlertMessage from '../../../components/AlertMessage'
import ProjectImportsChannel from '../../../ActionCable/channels/ProjectImportsChannel'
import PageHeader from '../../../components/PageHeader'
import PageLoader from '../../../components/PageLoader'
import ProjectForceImportConfirmModal from '../ProjectForceImportConfirmModal'
import projectImportsActions from './actions'
import projectsActions from '../actions'
import StepTimingsModal from './StepTimingsModal'
import TablePagination from '../../../components/TablePagination'

// Import steps in order of execution (same as SynchronizationStatus)
const IMPORT_STEPS = [
  'refreshing_source_code',
  'parsing_translation_files',
  'detecting_locales',
  'detecting_string_locales',
  'updating_existing_strings',
  'destroying_removed_strings',
  'creating_strings',
  'creating_strings_with_unknown_locale',
  'importing_values_from_other_locales',
  'detecting_unused_strings',
  'detecting_duplicated_strings'
]

// Steps that have granular progress tracking
const STEPS_WITH_PROGRESS = [
  'detecting_string_locales',
  'updating_existing_strings',
  'destroying_removed_strings',
  'creating_strings',
  'detecting_unused_strings',
  'detecting_duplicated_strings'
]

const getImportProgress = (importStep, importProgress = 0) => {
  const stepIndex = IMPORT_STEPS.indexOf(importStep)
  if (stepIndex < 0) return 0

  const hasGranularProgress = STEPS_WITH_PROGRESS.includes(importStep)
  const stepBaseProgress = Math.round((stepIndex / IMPORT_STEPS.length) * 100)
  const stepContribution = 100 / IMPORT_STEPS.length

  // For steps with granular progress, always use the granular calculation
  // This shows progress from start to end of the step (e.g., 30% to 40%)
  if (hasGranularProgress) {
    return Math.round(stepBaseProgress + ((importProgress * stepContribution) / 100))
  }

  // For steps without granular progress, show the end of the step
  return Math.round(((stepIndex + 1) / IMPORT_STEPS.length) * 100)
}

const ProjectImports = ({
  dispatch,
  fetchingAllError = null,
  fetchingAllStatus = 'idle',
  fetchingProjectStatus = 'idle',
  match: { params: { slug = null } = {} } = {},
  paginationCurrentPage = null,
  paginationTotalPages = null,
  project = null,
  projectImports = null
}) => {
  const { t } = useTranslation(['ProjectsPage', 'commons'])
  const [page, setPage] = useState(null)
  const [expandedRows, setExpandedRows] = useState({})
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [selectedImportForTimings, setSelectedImportForTimings] = useState(null)

  // Create ActionCable consumer once
  const consumer = useMemo(() => createConsumer(), [])

  const refetchImports = () => {
    dispatch(projectImportsActions.fetchAll(slug, page))
  }

  useEffect(() => {
    if (slug) {
      refetchImports()
    }
  }, [slug])

  // Fetch project if not loaded (needed for repositoryId in sync modal)
  useEffect(() => {
    if (!project && slug && fetchingProjectStatus === 'idle') {
      dispatch(projectsActions.fetch(slug))
    }
  }, [project, slug, fetchingProjectStatus])

  const handleSyncModalClose = () => {
    setShowSyncModal(false)
  }

  const moveToPage = (selected) => {
    setPage(selected)
    dispatch(projectImportsActions.fetchAll(slug, selected))
  }

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id]
    }))
  }

  const stateBadgeColor = (state) => {
    switch (state) {
      case 'pending': return 'warning'
      case 'importing': return 'primary'
      case 'importing_failed': return 'danger'
      case 'importing_succeeded': return 'success'
      case 'cancelled': return 'secondary'
      default: return 'secondary'
    }
  }

  const formatDuration = (stats) => {
    if (!stats?.duration) return '-'
    return moment.duration(stats.duration * 1000).humanize()
  }

  return (
    <>
      {slug && (
        <ProjectImportsChannel consumer={consumer} projectId={slug} />
      )}

      <Container fluid className="mt-3">
        <div className="d-flex flex-column">
          <div className="d-flex flex-row justify-content-between">
            <div className="d-flex flex-column">
              <PageHeader
                backLinkTo="all"
                projectSlug={slug}
                title={t('ProjectImports.Index.title')}
                withoutHr
              />
            </div>

            <Button
              color="primary"
              onClick={() => setShowSyncModal(true)}
            >
              {t('ProjectImports.Index.buttons.synchronize')}
            </Button>
          </div>

          <hr className="w-100" />
        </div>

        <div className="d-flex flex-column flex-fill justify-content-center">
          {['idle', 'fetching'].includes(fetchingAllStatus) && (
            <div className="d-flex flex-row justify-content-center">
              <PageLoader message={t('ProjectImports.Index.statuses.fetching')} />
            </div>
          )}

          {fetchingAllStatus === 'failed' && (
            <AlertMessage>
              {fetchingAllError || t('errors.somethingWentWrong', { ns: 'commons' })}
            </AlertMessage>
          )}

          {fetchingAllStatus === 'succeeded'
            && projectImports.length === 0
            && (
              <div className="d-flex flex-column align-items-center my-5">
                <p className="lead mb-1">
                  {t('ProjectImports.Index.messages.emptyTitle')}
                </p>
                <p className="text-muted">
                  {t('ProjectImports.Index.messages.emptyDescription')}
                </p>
              </div>
            )}

          {fetchingAllStatus === 'succeeded'
            && projectImports.length > 0
            && (
              <div className="d-flex flex-column">
                <Table>
                  <thead>
                    <tr>
                      <th>{t('ProjectImports.Index.tableColumns.when')}</th>
                      <th>{t('ProjectImports.Index.tableColumns.branch')}</th>
                      <th>{t('ProjectImports.Index.tableColumns.status')}</th>
                      <th>{t('ProjectImports.Index.tableColumns.duration')}</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {projectImports.map((projectImport) => (
                      <React.Fragment key={uuidv1()}>
                        <tr style={{ verticalAlign: 'middle' }}>
                          <td>{moment(projectImport.created_at).format('LLL')}</td>
                          <td>
                            <code>{projectImport.branch_name || '-'}</code>
                          </td>
                          <td>
                            <Badge color={stateBadgeColor(projectImport.state)}>
                              {t(`ProjectImports.Index.states.${projectImport.state}`)}
                            </Badge>
                          </td>
                          <td style={{ minWidth: '120px' }}>
                            {projectImport.state === 'importing' ? (
                              <div
                                className="position-relative"
                                style={{ height: '24px' }}
                              >
                                <Progress
                                  value={getImportProgress(
                                    projectImport.import_step,
                                    projectImport.import_progress
                                  )}
                                  style={{
                                    width: '100%',
                                    height: '6px',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)'
                                  }}
                                  animated
                                  striped
                                />
                                <small
                                  className="position-absolute"
                                  style={{
                                    fontSize: '0.65rem',
                                    padding: '2px 6px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.15)',
                                    color: '#333',
                                    fontWeight: '600',
                                    left: `${getImportProgress(
                                      projectImport.import_step,
                                      projectImport.import_progress
                                    )}%`,
                                    top: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    transition: 'left 0.3s ease-out',
                                    zIndex: 1
                                  }}
                                >
                                  {`${getImportProgress(
                                    projectImport.import_step,
                                    projectImport.import_progress
                                  )}%`}
                                </small>
                              </div>
                            ) : (
                              <div className="d-flex align-items-center">
                                <span>{formatDuration(projectImport.import_stats)}</span>
                                {projectImport.import_stats?.duration && (
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 ms-2"
                                    onClick={() => setSelectedImportForTimings(projectImport)}
                                    title={t('ProjectImports.Index.buttons.showStepTimings')}
                                  >
                                    <i className="bi bi-clock-history" />
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            {projectImport.state === 'importing_failed' && (
                              <div className="d-flex justify-content-end">
                                <Button
                                  color="link"
                                  onClick={() => toggleRow(projectImport.id)}
                                  size="sm"
                                >
                                  {expandedRows[projectImport.id]
                                    ? t('ProjectImports.Index.buttons.hideError')
                                    : t('ProjectImports.Index.buttons.showError')}
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {projectImport.state === 'importing_failed' && (
                          <tr>
                            <td
                              colSpan="5"
                              className="p-0 border-0"
                              style={{ maxWidth: 0 }}
                            >
                              <Collapse isOpen={expandedRows[projectImport.id]}>
                                <div
                                  className="p-3 bg-light"
                                  style={{ overflowX: 'auto' }}
                                >
                                  <strong>
                                    {t('ProjectImports.Index.errorDetails.reason')}
                                  </strong>
                                  <p className="mb-2">
                                    {projectImport.import_failure_reason || '-'}
                                  </p>
                                  {projectImport.import_failure_trace && (
                                    <>
                                      <strong>
                                        {t('ProjectImports.Index.errorDetails.trace')}
                                      </strong>
                                      <pre
                                        className="bg-dark text-light p-2 rounded"
                                        style={{
                                          fontSize: '0.75rem',
                                          maxHeight: '200px',
                                          overflow: 'auto'
                                        }}
                                      >
                                        {projectImport.import_failure_trace}
                                      </pre>
                                    </>
                                  )}
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>

                <div className="d-flex flex-row justify-content-center">
                  <TablePagination
                    currentPage={paginationCurrentPage}
                    onPaginationClick={(selected) => moveToPage(selected)}
                    totalPages={paginationTotalPages}
                  />
                </div>
              </div>
            )}
        </div>

        <ProjectForceImportConfirmModal
          isOpen={showSyncModal}
          onClose={handleSyncModalClose}
          project={project}
          projectId={slug}
          repositoryId={project?.repository_id}
        />

        <StepTimingsModal
          currentImport={selectedImportForTimings}
          isOpen={!!selectedImportForTimings}
          onClose={() => setSelectedImportForTimings(null)}
          projectImports={projectImports}
        />
      </Container>
    </>
  )
}

ProjectImports.propTypes = {
  dispatch: PropTypes.func.isRequired,
  fetchingAllError: PropTypes.string,
  fetchingAllStatus: PropTypes.string,
  fetchingProjectStatus: PropTypes.string,
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string
    })
  }),
  paginationCurrentPage: PropTypes.number,
  paginationTotalPages: PropTypes.number,
  project: PropTypes.shape({
    id: PropTypes.string,
    repository_id: PropTypes.number
  }),
  projectImports: PropTypes.arrayOf(PropTypes.shape({
    branch_name: PropTypes.string,
    created_at: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    import_failure_reason: PropTypes.string,
    import_failure_trace: PropTypes.string,
    import_stats: PropTypes.shape({
      duration: PropTypes.number
    }),
    state: PropTypes.string.isRequired,
    step_timings: PropTypes.objectOf(PropTypes.shape({
      duration: PropTypes.number
    }))
  }))
}

const mapStateToProps = (state, ownProps) => {
  const { slug } = ownProps.match?.params || {}
  // Check items first (from fetchAll), then item (from fetch single)
  const project = state.projects.items?.find((p) => p.id === slug)
    || (state.projects.item?.id === slug ? state.projects.item : null)

  return {
    fetchingAllError: state.projectImports.fetchingAllError,
    fetchingAllStatus: state.projectImports.fetchingAllStatus,
    fetchingProjectStatus: state.projects.fetchingStatus,
    paginationCurrentPage: state.projectImports.itemsCurrentPage,
    paginationTotalPages: state.projectImports.itemsTotalPages,
    project,
    projectImports: state.projectImports.items
  }
}

export default connect(mapStateToProps)(ProjectImports)
